import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/copy";

export function TableSearch({ label, value, onChange, count, total }: { label: string; value: string; onChange: (value: string) => void; count: number; total: number }) {
  return <div className="mb-3 flex flex-wrap items-center gap-3">
    <Input type="search" aria-label={label} placeholder={label} value={value} onChange={(event) => onChange(event.target.value)} className="w-full sm:w-80" />
    <span role="status" className="text-xs text-muted-foreground tabular-nums">{count} of {total}</span>
    {value && <Button variant="outline" size="sm" onClick={() => onChange("")}>Clear search</Button>}
  </div>;
}

// Long operator-controlled text stays bounded; full content is selectable and
// available to keyboard and touch users without relying on a hover tooltip.
export function TableText({ value }: { value: string }) {
  if (value.length <= 36) return <span>{value || "—"}</span>;
  return <details className="w-64 text-xs">
    <summary className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring" title={value}>
      <span className="min-w-0 truncate">{value}</span><span className="shrink-0 text-primary underline underline-offset-2">View</span>
    </summary>
    <div className="mt-2 space-y-2 rounded-md border bg-background p-2">
      <div className="whitespace-pre-wrap break-all select-text">{value}</div>
      <CopyButton value={value} />
    </div>
  </details>;
}

export function TablePager({ page, pageSize, total, onChange }: { page: number; pageSize: number; total: number; onChange: (page: number) => void }) {
  if (total <= pageSize) return null;
  return <nav aria-label="Table pagination" className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs">
    <span className="text-muted-foreground tabular-nums">{page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}</span>
    <div className="flex items-center gap-2">
      <Button size="sm" variant="outline" disabled={page === 0} onClick={() => onChange(page - 1)}>Previous page</Button>
      <span className="tabular-nums">{page + 1} / {Math.ceil(total / pageSize)}</span>
      <Button size="sm" variant="outline" disabled={(page + 1) * pageSize >= total} onClick={() => onChange(page + 1)}>Next page</Button>
    </div>
  </nav>;
}
